import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  OnChangeFn,
  RowSelectionState,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import { useToast } from '../contexts/ToastContext';
import { API_BASE } from '../config';
import { PANEL_TABLE_SCROLL_STYLE } from '../constants/layout';
import { useShiftClickRowSelection } from '../hooks/useShiftClickRowSelection';
import { toBcp47Locale } from '../locales/locale';
import { stripHtml } from '../utils/note-utils';
import { formatTimelineDate } from '../utils/timeline-date-format';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { StringTagsCell } from './StringTagsCell';
import { SortableTableHeader } from './SortableTableHeader';
import { DataTableRowCheckbox } from './DataTableRowCheckbox';
import { DataTableSelectAllCheckbox } from './DataTableSelectAllCheckbox';
import { RelatedEntityLink } from './RelatedEntityLink';
import type { RelatedEntity } from '../utils/entity-utils';

const REFERENCED_IN_PREVIEW_LIMIT = 5;

export interface TimelineItemRow {
  id: string;
  name: string;
  description: string | null;
  item_type: string;
  tags: string[];
  start_date: string;
  start_precision: string;
  start_timezone?: string | null;
  end_date: string | null;
  end_precision: string | null;
  end_timezone?: string | null;
  referencedIn?: RelatedEntity[];
}

interface TimelineItemsTableProps {
  items: TimelineItemRow[];
  projectId: string;
  canEditProject: boolean;
  itemTypeLabel: (itemType: string) => string;
  onRowClick: (item: TimelineItemRow) => void;
  onTagsUpdated: (itemId: string, tags: string[]) => void;
  onSeeAllReferencedIn?: (item: TimelineItemRow) => void;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
}

function compareTimelineDates(
  left: { date: string; precision: string },
  right: { date: string; precision: string }
): number {
  return new Date(left.date).getTime() - new Date(right.date).getTime();
}

export const TimelineItemsTable: React.FC<TimelineItemsTableProps> = ({
  items,
  projectId,
  canEditProject,
  itemTypeLabel,
  onRowClick,
  onTagsUpdated,
  onSeeAllReferencedIn,
  rowSelection: controlledSelection,
  onRowSelectionChange,
}) => {
  const { t, locale } = useLocale();
  const { token } = useAuth();
  const { showFieldSavedToast } = useToast();
  const dateLocale = toBcp47Locale(locale);
  const [sorting, setSorting] = useState<SortingState>([{ id: 'start_date', desc: false }]);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const selectionEnabled = Boolean(canEditProject && onRowSelectionChange);
  const getVisibleRowIdsRef = useRef<() => string[]>(() => []);
  const {
    rowSelection,
    setRowSelection,
    selectRangeTo,
    toggleRowSelected,
    setAnchorRowId,
    handleRowClick,
  } = useShiftClickRowSelection({
    getVisibleRowIds: () => getVisibleRowIdsRef.current(),
    ...(selectionEnabled
      ? {
          rowSelection: controlledSelection ?? {},
          onRowSelectionChange,
        }
      : {}),
  });

  useEffect(() => {
    if (!projectId || !token) {
      return;
    }
    const fetchTags = async (): Promise<void> => {
      try {
        const response = await fetch(`${API_BASE}/api/projects/${projectId}/tags`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          setAvailableTags(Array.isArray(data.tags) ? data.tags : []);
        }
      } catch {
        setAvailableTags([]);
      }
    };
    void fetchTags();
  }, [projectId, token]);

  const handleTagsChange = useCallback(
    async (itemId: string, nextTags: string[]): Promise<void> => {
      if (!projectId || !token) {
        return;
      }
      const response = await fetch(`${API_BASE}/api/projects/${projectId}/timeline-items/${itemId}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ tags: nextTags }),
      });
      if (!response.ok) {
        throw new Error('Failed to update tags');
      }
      onTagsUpdated(itemId, nextTags);
      showFieldSavedToast(t('Tags'));
    },
    [projectId, token, onTagsUpdated, showFieldSavedToast, t]
  );

  const columns = useMemo<ColumnDef<TimelineItemRow>[]>(
    () => [
      ...(selectionEnabled
        ? [
            {
              id: 'select',
              enableSorting: false,
              header: ({ table }) => (
                <DataTableSelectAllCheckbox
                  checked={
                    table.getIsAllPageRowsSelected()
                      ? true
                      : table.getIsSomePageRowsSelected()
                        ? 'indeterminate'
                        : false
                  }
                  onCheckedChange={(checked) => {
                    table.toggleAllPageRowsSelected(checked);
                    const visibleIds = getVisibleRowIdsRef.current();
                    setAnchorRowId(checked && visibleIds.length > 0 ? visibleIds[0] : null);
                  }}
                  label={t('timeline_select_all')}
                />
              ),
              cell: ({ row }) => (
                <DataTableRowCheckbox
                  checked={row.getIsSelected()}
                  rowId={row.id}
                  ariaLabel={t('timeline_select_row')}
                  onSelectRange={selectRangeTo}
                  onToggle={toggleRowSelected}
                />
              ),
            } satisfies ColumnDef<TimelineItemRow>,
          ]
        : []),
      {
        accessorKey: 'start_date',
        header: ({ column }) => <SortableTableHeader label={t('Start')} column={column} />,
        cell: ({ row }) => (
          <span className="text-muted-foreground">
            {formatTimelineDate(
              row.original.start_date,
              row.original.start_precision,
              dateLocale,
              row.original.start_timezone
            )}
          </span>
        ),
        sortingFn: (rowA, rowB) =>
          compareTimelineDates(
            { date: rowA.original.start_date, precision: rowA.original.start_precision },
            { date: rowB.original.start_date, precision: rowB.original.start_precision }
          ),
      },
      {
        id: 'end_date',
        accessorFn: (row) => row.end_date ?? '',
        header: ({ column }) => <SortableTableHeader label={t('End')} column={column} />,
        cell: ({ row }) => (
          <span className="text-muted-foreground">
            {row.original.end_date && row.original.end_precision
              ? formatTimelineDate(
                  row.original.end_date,
                  row.original.end_precision,
                  dateLocale,
                  row.original.end_timezone
                )
              : '—'}
          </span>
        ),
        sortingFn: (rowA, rowB) => {
          const endA = rowA.original.end_date;
          const endB = rowB.original.end_date;
          if (!endA && !endB) return 0;
          if (!endA) return 1;
          if (!endB) return -1;
          return compareTimelineDates(
            { date: endA, precision: rowA.original.end_precision ?? 'day' },
            { date: endB, precision: rowB.original.end_precision ?? 'day' }
          );
        },
      },
      {
        accessorKey: 'name',
        header: ({ column }) => (
          <SortableTableHeader label={t('timeline_event_name_placeholder')} column={column} />
        ),
        cell: ({ row }) => <span className="font-medium">{row.original.name}</span>,
      },
      {
        accessorKey: 'description',
        header: ({ column }) => <SortableTableHeader label={t('description')} column={column} />,
        cell: ({ row }) => {
          const text = stripHtml(row.original.description ?? '');
          if (!text) {
            return <span className="text-muted-foreground">—</span>;
          }
          return (
            <span className="line-clamp-2 break-words text-muted-foreground" title={text}>
              {text}
            </span>
          );
        },
        sortingFn: (rowA, rowB) =>
          stripHtml(rowA.original.description ?? '').localeCompare(
            stripHtml(rowB.original.description ?? '')
          ),
      },
      {
        id: 'referenced_in',
        accessorFn: (row) => row.referencedIn?.length ?? 0,
        header: ({ column }) => (
          <SortableTableHeader label={t('referenced_in')} column={column} />
        ),
        cell: ({ row }) => {
          const entities = row.original.referencedIn ?? [];
          if (entities.length === 0) {
            return <span className="text-muted-foreground">—</span>;
          }
          const visibleEntities = entities.slice(0, REFERENCED_IN_PREVIEW_LIMIT);
          const hasMore = entities.length > REFERENCED_IN_PREVIEW_LIMIT;
          return (
            <div
              className="flex max-w-xs flex-wrap items-center gap-1.5"
              onClick={(event) => event.stopPropagation()}
            >
              {visibleEntities.map((entity) => (
                <RelatedEntityLink
                  key={`${entity.type}-${entity.id}-${entity.documentId ?? ''}`}
                  entity={entity}
                  projectId={projectId}
                />
              ))}
              {hasMore && onSeeAllReferencedIn ? (
                <button
                  type="button"
                  className="text-xs font-medium text-scribane-green hover:underline"
                  onClick={() => onSeeAllReferencedIn(row.original)}
                >
                  {t('see_all')}
                </button>
              ) : null}
            </div>
          );
        },
        sortingFn: (rowA, rowB) =>
          (rowA.original.referencedIn?.length ?? 0) -
          (rowB.original.referencedIn?.length ?? 0),
      },
      {
        accessorKey: 'item_type',
        header: ({ column }) => <SortableTableHeader label={t('timeline_item_type')} column={column} />,
        cell: ({ row }) => (
          <span className="text-muted-foreground">{itemTypeLabel(row.original.item_type)}</span>
        ),
        sortingFn: (rowA, rowB) =>
          itemTypeLabel(rowA.original.item_type).localeCompare(itemTypeLabel(rowB.original.item_type)),
      },
      {
        accessorKey: 'tags',
        header: () => t('timeline_filter_by_tags'),
        enableSorting: false,
        cell: ({ row }) => (
          <div onClick={(e) => e.stopPropagation()}>
            <StringTagsCell
              tags={row.original.tags}
              availableTags={availableTags}
              readOnly={!canEditProject}
              onTagsChange={(nextTags) => handleTagsChange(row.original.id, nextTags)}
            />
          </div>
        ),
      },
    ],
    [
      availableTags,
      canEditProject,
      dateLocale,
      handleTagsChange,
      itemTypeLabel,
      onSeeAllReferencedIn,
      projectId,
      selectRangeTo,
      selectionEnabled,
      setAnchorRowId,
      t,
      toggleRowSelected,
    ]
  );

  const table = useReactTable({
    data: items,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    enableRowSelection: selectionEnabled,
    onRowSelectionChange: selectionEnabled ? setRowSelection : undefined,
    getRowId: (row) => row.id,
    state: {
      sorting,
      ...(selectionEnabled ? { rowSelection } : {}),
    },
  });

  getVisibleRowIdsRef.current = (): string[] =>
    table.getRowModel().rows.map((row) => row.id);

  return (
    <div style={PANEL_TABLE_SCROLL_STYLE} className="text-card-foreground">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  className={header.column.id === 'select' ? 'w-10' : undefined}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.id}
              className={canEditProject ? 'cursor-pointer hover:bg-muted/30' : undefined}
              onClick={(event) => {
                if (!canEditProject) {
                  return;
                }
                if (selectionEnabled) {
                  handleRowClick(row.id, event, () => onRowClick(row.original));
                  return;
                }
                onRowClick(row.original);
              }}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell
                  key={cell.id}
                  className={
                    cell.column.id === 'description'
                      ? 'max-w-0'
                      : cell.column.id === 'select'
                        ? 'w-10'
                        : undefined
                  }
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
